import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  IconShieldCheck,
  IconMail,
  IconArrowRight,
  IconLock,
  IconBrandWindows,
  IconBuilding,
  IconTool,
  IconClipboardList,
  IconDroplet,
  IconCar,
  IconAlertTriangle,
} from '@tabler/icons-react';

const roleConfig = {
  manager: {
    label: 'Branch manager',
    colour: '#185FA5',
    bg: '#E6F1FB',
    icon: IconClipboardList,
  },
  fuel_manager: {
    label: 'Fuel manager',
    colour: '#085041',
    bg: '#E1F5EE',
    icon: IconDroplet,
  },
  operator: {
    label: 'Operator',
    colour: '#3B6D11',
    bg: '#EAF3DE',
    icon: IconTool,
  },
  driver: {
    label: 'Driver',
    colour: '#534AB7',
    bg: '#EEEDFE',
    icon: IconCar,
  },
};

export default function InvitationAcceptance() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = getStrength(password);
  const strengthColours = ['', 'var(--color-danger)', 'var(--color-warning)', '#FAC775', 'var(--color-success)'];
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const inviteDoc = await getDoc(doc(db, 'invites', token));
        if (!inviteDoc.exists()) {
          setInvalid(true);
          setLoading(false);
          return;
        }

        const inviteData = inviteDoc.data();

        if (inviteData.accepted) {
          setInvalid(true);
          setLoading(false);
          return;
        }

        setInvite(inviteData);
        setEmail(inviteData.email || '');

        const orgDoc = await getDoc(doc(db, 'organisations', inviteData.orgId));
        if (orgDoc.exists()) {
          setOrg({ id: orgDoc.id, ...orgDoc.data() });
        }
      } catch (err) {
        console.error(err);
        setInvalid(true);
      }
      setLoading(false);
    };
    loadInvite();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      } catch (err) {
        if (err.code === 'auth/email-already-in-use') {
          userCredential = await signInWithEmailAndPassword(auth, email, password);
        } else throw err;
      }

      const uid = userCredential.user.uid;

      await updateDoc(
        doc(db, 'organisations', invite.orgId, 'users', uid),
        {
          name: `${firstName} ${lastName}`,
          email,
          role: invite.role,
          orgId: invite.orgId,
          branchId: invite.branchId || null,
          departmentId: invite.departmentId || null,
          level: invite.level || 'branch',
          activatedAt: serverTimestamp(),
          onboardingComplete: false,
        }
      );

      await updateDoc(doc(db, 'invites', token), {
        accepted: true,
        acceptedAt: serverTimestamp(),
        acceptedBy: uid,
      });

      if (invite.role === 'manager') {
        navigate('/onboarding/manager');
      } else if (invite.role === 'fuel_manager') {
        navigate('/onboarding/fuel');
      } else if (invite.role === 'senior_manager') {
        navigate('/onboarding/senior');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      switch (err.code) {
        case 'auth/weak-password':
          setError('Password must be at least 6 characters');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password for existing account');
          break;
        default:
          setError('Something went wrong — please try again');
      }
      setSubmitting(false);
    }
  };

  if (loading) return null;

  if (invalid) return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg-tertiary)',
    }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        border: '0.5px solid var(--color-border-light)',
        borderRadius: 'var(--radius-xl)',
        padding: '40px 48px',
        maxWidth: 400,
        textAlign: 'center',
      }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--color-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <IconAlertTriangle size={24} color="var(--color-danger)" stroke={1.5} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 10 }}>Invalid invitation</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
          This invitation link is invalid or has already been used. Please contact your administrator for a new invitation.
        </div>
        <button
          onClick={() => navigate('/login')}
          style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer' }}
        >
          Go to login
        </button>
      </div>
    </div>
  );

  const role = roleConfig[invite?.role] || roleConfig.manager;
  const RoleIcon = role.icon;
  const brandColour = org?.brandColour || 'var(--color-brand)';
  const isTabletRole = invite?.role === 'operator' || invite?.role === 'driver';

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel */}
      <div style={{
        width: '40%',
        background: brandColour,
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'background 0.3s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>{org?.name || 'Veritas'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <IconMail size={26} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 400, color: '#fff', lineHeight: 1.35, marginBottom: 14 }}>
            You've been invited to {org?.name} on Veritas.
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, marginBottom: 28 }}>
            {isTabletRole
              ? 'Set up your account then download the Veritas app on your tablet to get started.'
              : 'Set up your account to get started. Your branch and department have already been assigned.'
            }
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {invite?.branchName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                <IconBuilding size={15} stroke={1.5} color="rgba(255,255,255,0.5)" />
                {invite.branchName} branch
              </div>
            )}
            {invite?.departmentId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                <IconTool size={15} stroke={1.5} color="rgba(255,255,255,0.5)" />
                {invite.departmentId} department
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
              <RoleIcon size={15} stroke={1.5} color="rgba(255,255,255,0.5)" />
              {role.label} role
            </div>
          </div>
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
        overflow: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          <div style={{
            background: 'var(--color-brand-light)',
            border: '0.5px solid var(--color-brand-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 28,
          }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#FDF0E8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              color: '#993C1D',
              flexShrink: 0,
            }}>
              {invite?.invitedByName?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>
                <strong>{invite?.invitedByName}</strong> has invited you to join{' '}
                <strong>{org?.name}</strong> on Veritas
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                background: role.bg,
                color: role.colour,
                borderRadius: 'var(--radius-full)',
                padding: '2px 8px',
                marginTop: 6,
              }}>
                <RoleIcon size={11} stroke={1.5} />
                {role.label}
                {invite?.branchName && ` · ${invite.branchName}`}
                {invite?.departmentId && ` · ${invite.departmentId}`}
              </div>
            </div>
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 6 }}>Set up your account</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
            {isTabletRole
              ? "Confirm your details and set a password. You'll use these to sign in on the Veritas tablet app."
              : 'Confirm your details and set a password to get started.'
            }
          </p>

          <form onSubmit={handleAccept}>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>First name</label>
                <input type="text" placeholder="Sarah" value={firstName} onChange={e => setFirstName(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>Last name</label>
                <input type="text" placeholder="Mitchell" value={lastName} onChange={e => setLastName(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>Work email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>

            {(invite?.branchName || invite?.departmentId) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {invite?.branchName && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <label style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Branch</label>
                      <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <IconLock size={10} stroke={1.5} /> Locked
                      </span>
                    </div>
                    <div style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {invite.branchName}
                      <IconLock size={13} stroke={1.5} color="var(--color-text-tertiary)" style={{ opacity: 0.4 }} />
                    </div>
                  </div>
                )}
                {invite?.departmentId && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <label style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Department</label>
                      <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <IconLock size={10} stroke={1.5} /> Locked
                      </span>
                    </div>
                    <div style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 13, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {invite.departmentId}
                      <IconLock size={13} stroke={1.5} color="var(--color-text-tertiary)" style={{ opacity: 0.4 }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>Password</label>
              <input type="password" placeholder="Min. 8 characters" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} style={{ width: '100%', boxSizing: 'border-box' }} />
              {password && (
                <>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= strength ? strengthColours[strength] : 'var(--color-bg-secondary)', transition: 'background 0.2s' }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: strengthColours[strength], marginTop: 4 }}>{strengthLabels[strength]}</div>
                </>
              )}
            </div>

            {error && (
              <div style={{ fontSize: 12, color: 'var(--color-danger)', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 16 }}>
                {error}
              </div>
            )}

            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'center', marginBottom: 14, lineHeight: 1.5 }}>
              By accepting this invitation you agree to Veritas'{' '}
              <span style={{ color: 'var(--color-brand)', cursor: 'pointer' }}>Terms of Service</span>
            </div>

            <button
              type="button"
              style={{ width: '100%', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '11px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}
            >
              <div style={{ width: 16, height: 16, background: '#0078D4', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconBrandWindows size={10} color="white" stroke={1.5} />
              </div>
              Accept with Microsoft 365
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>or</span>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{ width: '100%', background: brandColour, color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '12px', fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              {submitting ? 'Accepting...' : 'Accept invitation'}
              {!submitting && <IconArrowRight size={16} stroke={1.5} />}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}